import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { api, formatApiError } from "@/lib/api";
import { useClinic } from "@/context/ClinicContext";
import EmptyState from "@/components/EmptyState";
import { CardSkeleton, Skeleton } from "@/components/Skeleton";
import { useLocations } from "@/lib/useLocations";
import { locationsFromSlots, filterSlotsByLocation } from "@/lib/locations";
import { CalendarPlus, CalendarX2, Check, ChevronLeft, Clock, Loader2, MapPin } from "lucide-react";

/** Local day key (YYYY-MM-DD) for an instant, in the clinic's timezone. */
function dayKey(startUtc, tz) {
  return new Date(startUtc).toLocaleDateString("en-CA", { timeZone: tz });
}

function dayLabel(startUtc, tz) {
  return new Date(startUtc).toLocaleDateString(undefined, {
    weekday: "long", month: "short", day: "numeric", timeZone: tz,
  });
}

function timeLabel(startUtc, tz) {
  return new Date(startUtc).toLocaleTimeString(undefined, {
    hour: "numeric", minute: "2-digit", timeZone: tz,
  });
}

export default function PatientBook() {
  const { clinic } = useClinic();
  const tz = clinic?.timezone || "America/New_York";
  const bookingEnabled = clinic?.booking_enabled !== false;

  const [visitTypes, setVisitTypes] = useState(null);
  const [visitType, setVisitType] = useState(null);
  const [providerId, setProviderId] = useState("");
  // Where, asked before when — but only when this visit type is offered at more than one
  // site. A single-site clinic's patients never see this step at all.
  const { locations } = useLocations({ portal: "patient" });
  const [locationId, setLocationId] = useState("");
  const [slots, setSlots] = useState(null);
  const [loadingSlots, setLoadingSlots] = useState(false);
  const [booking, setBooking] = useState("");
  const [confirmed, setConfirmed] = useState(null);
  const [error, setError] = useState("");

  useEffect(() => {
    api.get("/patient/visit-types")
      .then((r) => setVisitTypes(r.data))
      .catch((e) => { setVisitTypes([]); setError(formatApiError(e.response?.data?.detail)); });
  }, []);

  useEffect(() => {
    if (!visitType) return;
    setLoadingSlots(true);
    setError("");
    const params = { visit_type_id: visitType.id };
    if (providerId) params.provider_id = providerId;
    api.get("/availability", { params })
      .then((r) => setSlots(r.data.slots || []))
      .catch((e) => { setSlots([]); setError(formatApiError(e.response?.data?.detail)); })
      .finally(() => setLoadingSlots(false));
  }, [visitType, providerId]);

  // Providers offering this visit type, derived from what is actually bookable.
  const providers = useMemo(() => {
    const seen = new Map();
    (slots || []).forEach((s) => {
      if (s.provider_id && !seen.has(s.provider_id)) seen.set(s.provider_id, s.provider_name);
    });
    return [...seen.entries()].map(([id, name]) => ({ id, name }));
  }, [slots]);

  const offeredLocations = useMemo(() => locationsFromSlots(slots), [slots]);
  const needsLocationStep = offeredLocations.length >= 2 && !locationId;
  const shownSlots = useMemo(
    () => filterSlotsByLocation(slots || [], locationId), [slots, locationId]);
  // The address is what a patient actually needs; slots only carry the name.
  const locationDetail = (id) => (locations || []).find((l) => l.id === id) || null;

  const byDay = useMemo(() => {
    const groups = new Map();
    (shownSlots || []).forEach((s) => {
      const k = dayKey(s.start_utc, tz);
      if (!groups.has(k)) groups.set(k, []);
      groups.get(k).push(s);
    });
    return [...groups.entries()];
  }, [shownSlots, tz]);

  async function book(slot) {
    setBooking(slot.start_utc);
    setError("");
    try {
      const r = await api.post("/patient/appointments", {
        visit_type_id: visitType.id,
        provider_id: slot.provider_id,
        location_id: slot.location_id,
        start_utc: slot.start_utc,
      });
      setConfirmed(r.data);
    } catch (e) {
      setError(formatApiError(e.response?.data?.detail));
      // The slot may have just gone: re-render availability rather than leave a stale grid.
      api.get("/availability", { params: { visit_type_id: visitType.id } })
        .then((r) => setSlots(r.data.slots || [])).catch(() => {});
    } finally {
      setBooking("");
    }
  }

  if (confirmed) {
    const isRequest = confirmed.status === "requested";
    return (
      <div className="space-y-6" data-testid="book-page">
        <div className="bg-card border border-border rounded-xl p-8 text-center" data-testid="booking-success">
          <div className={`w-12 h-12 rounded-full flex items-center justify-center mx-auto mb-4 ${isRequest ? "bg-amber-100" : "bg-emerald-100"}`}>
            {isRequest ? <Clock className="w-6 h-6 text-amber-700" /> : <Check className="w-6 h-6 text-emerald-700" />}
          </div>
          <h1 className="font-heading text-xl font-bold">
            {isRequest ? "Request sent" : "You're booked"}
          </h1>
          <p className="text-sm text-muted-foreground mt-2 max-w-md mx-auto">
            {isRequest
              ? "We'll confirm shortly. You'll see it here as soon as the clinic approves it — the time isn't held until then."
              : confirmed.message || "We'll send a reminder before your visit."}
          </p>
          <p className="text-sm font-medium mt-4">
            {confirmed.type} · {dayLabel(confirmed.start_utc, tz)} at {timeLabel(confirmed.start_utc, tz)}
          </p>
          {locationDetail(confirmed.location_id) && (
            <p className="text-sm text-muted-foreground mt-1.5 inline-flex items-center gap-1.5"
               data-testid="booking-location">
              <MapPin className="w-3.5 h-3.5" />
              {[locationDetail(confirmed.location_id).name,
                locationDetail(confirmed.location_id).address].filter(Boolean).join(" · ")}
            </p>
          )}
          <div className="mt-6 flex items-center justify-center gap-3">
            <Link to="/portal/appointments" data-testid="booking-success-view"
                  className="px-4 py-2 rounded-md bg-primary text-primary-foreground text-sm font-medium">
              View my appointments
            </Link>
            <button onClick={() => { setConfirmed(null); setVisitType(null); setSlots(null); }}
                    className="px-4 py-2 rounded-md border border-border text-sm font-medium">
              Book another
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="book-page">
      <div>
        <h1 className="font-heading text-2xl font-bold tracking-tight">Book a visit</h1>
        <p className="text-sm text-muted-foreground mt-1">
          Pick what you need, then a time that suits you.
        </p>
      </div>

      {!bookingEnabled && (
        <EmptyState icon={CalendarX2} title="Online booking is turned off"
                    description="Message the clinic and we'll find you a time."
                    testid="booking-disabled" />
      )}

      {bookingEnabled && (
        <>
          {/* Step 1 — visit type */}
          {!visitType && (
            visitTypes === null ? <CardSkeleton lines={3} />
              : visitTypes.length === 0 ? (
                <EmptyState icon={CalendarX2} title="Nothing available to book online"
                            description="Message the clinic and we'll help." testid="no-visit-types" />
              ) : (
                <div className="grid gap-3 sm:grid-cols-2" data-testid="visit-type-list">
                  {visitTypes.map((vt) => (
                    <button key={vt.id} data-testid={`visit-type-${vt.id}`}
                            onClick={() => { setVisitType(vt); setSlots(null); setProviderId(""); setLocationId(""); }}
                            className="text-left bg-card border border-border rounded-xl p-5 hover:border-primary hover:shadow-sm transition-all">
                      <span className="font-medium">{vt.name}</span>
                      <p className="text-xs text-muted-foreground mt-1">
                        {vt.duration_min} minutes
                        {vt.booking_mode === "request" && " · we'll confirm this one"}
                      </p>
                    </button>
                  ))}
                </div>
              )
          )}

          {/* Step 2 — provider + slots */}
          {visitType && (
            <div className="space-y-4">
              <div className="flex flex-wrap items-center justify-between gap-3">
                <button onClick={() => { setVisitType(null); setSlots(null); }}
                        data-testid="book-back"
                        className="inline-flex items-center gap-1 text-sm text-muted-foreground hover:text-foreground">
                  <ChevronLeft className="w-4 h-4" /> {visitType.name}
                </button>
                {providers.length > 1 && (
                  <select data-testid="provider-select" value={providerId}
                          onChange={(e) => setProviderId(e.target.value)}
                          className="text-sm border border-border rounded-md px-3 py-1.5 bg-card">
                    <option value="">No preference</option>
                    {providers.map((p) => <option key={p.id} value={p.id}>{p.name}</option>)}
                  </select>
                )}
              </div>

              {visitType.booking_mode === "request" && (
                <p className="text-xs bg-amber-50 border border-amber-200 text-amber-900 rounded-lg px-3 py-2">
                  This visit needs a quick confirmation from the clinic, so the time isn't
                  held until we approve it.
                </p>
              )}

              {error && (
                <p className="text-sm text-destructive bg-destructive/10 border border-destructive/20 rounded-lg px-3 py-2"
                   data-testid="book-error">{error}</p>
              )}

              {/* Step: where. Skipped entirely when only one site offers this visit. */}
              {!loadingSlots && slots !== null && needsLocationStep ? (
                <div className="space-y-3" data-testid="location-step">
                  <p className="text-sm font-medium">Which location suits you?</p>
                  <div className="grid gap-3 sm:grid-cols-2">
                    {offeredLocations.map((l) => {
                      const detail = locationDetail(l.id);
                      return (
                        <button key={l.id} data-testid={`location-option-${l.id}`}
                                onClick={() => setLocationId(l.id)}
                                className="text-left bg-card border border-border rounded-xl p-5 hover:border-primary hover:shadow-sm transition-all">
                          <span className="font-medium inline-flex items-center gap-1.5">
                            <MapPin className="w-4 h-4 text-muted-foreground" /> {l.name}
                          </span>
                          {detail?.address && (
                            <p className="text-xs text-muted-foreground mt-1">{detail.address}</p>
                          )}
                        </button>
                      );
                    })}
                  </div>
                </div>
              ) : loadingSlots || slots === null ? (
                <div className="space-y-3"><Skeleton className="h-5 w-40" /><Skeleton className="h-24 w-full" /></div>
              ) : shownSlots.length === 0 ? (
                <EmptyState icon={CalendarX2} title="No open times in the next few weeks"
                            description="Message the clinic and we'll find something that works."
                            testid="no-slots" />
              ) : (
                <div className="space-y-5" data-testid="slot-list">
                  {locationId && offeredLocations.length >= 2 && (
                    <p className="text-xs text-muted-foreground inline-flex items-center gap-1.5"
                       data-testid="chosen-location">
                      <MapPin className="w-3 h-3" />
                      {locationDetail(locationId)?.name || ""}
                      <button onClick={() => setLocationId("")} data-testid="change-location"
                              className="text-primary hover:underline">change</button>
                    </p>
                  )}
                  {byDay.map(([day, daySlots]) => (
                    <div key={day}>
                      <p className="text-sm font-medium mb-2">{dayLabel(daySlots[0].start_utc, tz)}</p>
                      <div className="flex flex-wrap gap-2">
                        {daySlots.map((s) => (
                          <button key={`${s.provider_id}-${s.start_utc}`}
                                  data-testid={`slot-${s.start_utc}`}
                                  disabled={!!booking}
                                  onClick={() => book(s)}
                                  title={s.provider_name}
                                  className="px-3 py-2 rounded-md border border-border bg-card text-sm hover:border-primary hover:text-primary transition-all disabled:opacity-50">
                            {booking === s.start_utc
                              ? <Loader2 className="w-4 h-4 animate-spin" />
                              : timeLabel(s.start_utc, tz)}
                          </button>
                        ))}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}
        </>
      )}

      <p className="text-xs text-muted-foreground inline-flex items-center gap-1.5">
        <CalendarPlus className="w-3 h-3" /> Times are shown in the clinic's local time.
      </p>
    </div>
  );
}
